/**
 * @fileoverview entry point for your component library
 *
 * This is the entry point for your component library. Use this file to export utilities,
 * constants or data structure that accompany your components.
 *
 * DO NOT use this file to export your components. Instead, use the recommended approaches
 * to consume components of this package as outlined in the `README.md`.
 */
export { format } from './utils/utils';
export type * from './components.d.ts';

export const initializeChat = function(props: {
  baseUrl?: string;
  hostUrl?: string;
  licenseKey: string;
  appSecret: string;
  userSecret: string;
  userToken: string;
  isometrikUserId: string;
  projectId: string;
  keysetId: string;
  accountId: string;
  defaultTheme?: 'dark' | 'light';
  themeUrl?: string; // Single theme file URL
  containerId?: string;
  leofferAccountId?: string;
  getPaymentsData?: Function;
  handleBackButtonClick?: Function;
}) {
  if (
    !props.licenseKey ||
    !props.appSecret ||
    !props.userSecret ||
    !props.userToken ||
    !props.isometrikUserId ||
    !props.projectId ||
    !props.keysetId ||
    !props.accountId
  ) {
    console.error('Missing required props to Initialize chat!');
    return;
  }
  
  // First create the theme provider wrapper
  const themeProvider = document.createElement('theme-provider');
  
  // Set default theme mode
  if (props.defaultTheme) {
    themeProvider.setAttribute('mode', props.defaultTheme);
  }
  
  // Set the theme path based on priority:
  // 1. Custom theme path if provided
  // 2. Single theme URL (with both light/dark variants)
 if (props.themeUrl) {
    themeProvider.setAttribute('theme-path', props.themeUrl);
  } else {
    // Default theme URL with both themes
    themeProvider.setAttribute('theme-path', 'https://isometrik-website-bucket.s3.ap-south-1.amazonaws.com/styles/chat-combined-theme.css');
  }
  
  // Create the chat component
  const chatComponent = document.createElement('web-chat');
  
  // Set all the properties
  chatComponent.baseUrl = props?.baseUrl || 'https://apis.isometrik.io';
  chatComponent.hostUrl = props?.hostUrl || 'wss://connections.isometrik.io:2053/mqtt';
  chatComponent.licenseKey = props.licenseKey;
  chatComponent.appSecret = props.appSecret;
  chatComponent.userSecret = props.userSecret;
  chatComponent.userToken = props.userToken;
  chatComponent.isometrikUserId = props.isometrikUserId;
  chatComponent.projectId = props.projectId;
  chatComponent.keysetId = props.keysetId;
  chatComponent.accountId = props.accountId;
  chatComponent.leofferAccountId = props.leofferAccountId;  chatComponent.defaultTheme = props.defaultTheme || 'light';
  chatComponent.themeUrl = props.themeUrl || 'https://isometrik-website-bucket.s3.ap-south-1.amazonaws.com/styles/chat-combined-theme.css';
  chatComponent.getPaymentsData = props.getPaymentsData;
  chatComponent.containerId = props.containerId;
  chatComponent.handleBackButtonClick = props.handleBackButtonClick;
  // Set the style to take 100% width and height of the parent container
  chatComponent.style.width = '100%';
  chatComponent.style.height = '100%';
  
  // Append the chat component to the theme provider
  themeProvider.appendChild(chatComponent);
  
  // Set styles for the theme provider as well
  themeProvider.style.width = '100%';
  themeProvider.style.height = '100%';
  themeProvider.style.display = 'flex';
  themeProvider.style.flex = '1';

  if(window.innerWidth <= 768) {
    themeProvider.style.flexDirection = 'column';
  }
  
  // Find the container and append the theme provider to it
  const container = props.containerId ? document.getElementById(props.containerId) : document.body;
  if (container) {
    container.appendChild(themeProvider);
  } else {
    console.error('Container with the specified ID not found. Appending to body instead.');
    document.body.appendChild(themeProvider);
  }
  
  // Store reference to components for later use
  if (typeof window !== 'undefined') {
    (window as any).__isometrikChatComponents = (window as any).__isometrikChatComponents || {};
    (window as any).__isometrikChatComponents.themeProvider = themeProvider;
    (window as any).__isometrikChatComponents.chatComponent = chatComponent;
  }
};

export const customChatSidebar = function(props: {
  isChatFilter?: boolean
}) {
  const chatComponent = document.querySelector('web-chat');
  if(!chatComponent) {
    console.error("Isometrik chat is not initialized");
    return;
  }
  
  if (props.isChatFilter !== undefined) {
    chatComponent.isChatFilter = props.isChatFilter;
  }
};

export const switchChatTheme = function(theme: 'dark' | 'light') {
  const chatElements = document.getElementsByTagName('web-chat');
  
  for (let i = 0; i < chatElements.length; i++) {
    const chatWeb = chatElements[i];
    chatWeb.defaultTheme = theme;
    
    // Update the theme provider mode instead of changing the theme path
    const themeProvider = (window as any).__isometrikChatComponents?.themeProvider;
    if (themeProvider) {
      themeProvider.setAttribute('mode', theme);
    }
  }
};

// New function to apply a custom theme
export const applyCustomTheme = function(customTheme: {
  themePath?: string;
  themeObject?: { [key: string]: string };
}) {
  const themeProvider = document.querySelector('theme-provider');
  if (!themeProvider) {
    console.error("Theme provider is not initialized");
    return;
  }
  
  if (customTheme.themePath) {
    themeProvider.setAttribute('theme-path', customTheme.themePath);
  }
  
  if (customTheme.themeObject) {
    (themeProvider as any).themeObject = customTheme.themeObject;
  }
};

// New function to toggle between light and dark themes
export const toggleTheme = function() {
  const themeProvider = document.querySelector('theme-provider');
  if (!themeProvider) {
    console.error("Theme provider is not initialized");
    return;
  }

  // Use the built-in toggleTheme method of theme-provider
  themeProvider.toggleTheme();
};

if (typeof window !== 'undefined') {
  (window as any).initializeChat = initializeChat;
  (window as any).chatSidebarProps = customChatSidebar;
  (window as any).switchChatTheme = switchChatTheme;
  (window as any).applyCustomTheme = applyCustomTheme;
  (window as any).toggleTheme = toggleTheme; // Expose the toggle function
}